import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Grid, Skeleton, Card, CardContent, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { ArrowForward as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../../config/axiosConfig';
import { styled } from '@mui/material/styles';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import ConfirmationDialog from '../ConfirmationDialog';
import RTL from '../../../../utils/RTL';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { useAuth } from '../../../../context/AuthContext';
import { debounce } from '../../../../utils/debounce';
import FilterPanel from './FilterPanel';
import EnrollmentCard from './EnrollmentCard';
import OperationForm from './OperationForm';
import OperationsTable from './OperationsTable';
import { useStudentCardFilters } from './hooks/useStudentCardFilters';
import { useStudentCardData } from './hooks/useStudentCardData';
import { useEnrollmentOperations } from './hooks/useEnrollmentOperations';
import { useOperationForm } from './hooks/useOperationForm';

const CardGrid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const StudentCardBox = styled(Card)(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.main}`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        borderColor: theme.palette.primary.main,
    },
    '&:active': {
        transform: 'translateY(-1px)',
    }
}));

export default function StudentCard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [operationToDelete, setOperationToDelete] = useState(null);
    const [opDeleting, setOpDeleting] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState({ type: '', text: '' });
    const [renewSubmitting, setRenewSubmitting] = useState(false);
    const [renewError, setRenewError] = useState('');

    // Use custom hooks
    const {
        filters,
        isTyping,
        setIsTyping,
        handleFilterChange,
        handleDayChange,
        handleCourseChange,
        hasActiveFilters,
        handleClearFilters,
        buildParams
    } = useStudentCardFilters();

    const {
        data,
        loading,
        error,
        fetchData
    } = useStudentCardData(buildParams);

    const {
        operations,
        totalCount: operationsCount,
        loading: operationsLoading,
        error: operationsError,
        page: operationsPage,
        pageSize: operationsPageSize,
        statusFilter: operationsStatusFilter,
        setPage: setOperationsPage,
        setPageSize: setOperationsPageSize,
        setStatusFilter: setOperationsStatusFilter,
        fetchOperations,
        deleteOperation
    } = useEnrollmentOperations(selectedEnrollment?.id);

    const handleOperationSuccess = useCallback(async () => {
        await fetchData();
        if (selectedEnrollment?.id) {
            await fetchOperations(selectedEnrollment.id);
        }
    }, [fetchData, fetchOperations, selectedEnrollment]);

    const {
        opForm,
        submitting: opSubmitting,
        error: opError,
        message: opMessage,
        staleFee,
        clearStaleFee,
        submitWithOldFee,
        handleChange: handleOpChange,
        submit: submitOperation,
        clearMessage: clearOpMessage
    } = useOperationForm(selectedEnrollment, handleOperationSuccess, handleOperationSuccess);

    const enrollments = data?.results || [];
    const count = data?.count || 0;
    const isManager = (user?.staff_role?.name || '').toLowerCase() === 'manager';

    // Initial load on component mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Debounced refetch function
    const debouncedRefetch = useMemo(
        () => debounce(() => {
            setIsTyping(false);
            fetchData();
        }, 1000),
        [fetchData, setIsTyping]
    );

    // Auto-refetch when filters change (with debouncing for text inputs)
    useEffect(() => {
        // For text inputs (student), use debounced refetch
        if (filters.student) {
            debouncedRefetch();
        } else {
            // For other filters (dropdowns, dates, numbers), refetch immediately
            setIsTyping(false);
            fetchData();
        }
    }, [filters, debouncedRefetch, fetchData, setIsTyping]);

    const handleOpenOperation = useCallback((enrollment) => {
        setSelectedEnrollment(enrollment);
    }, []);

    const closeStaleFeeDialog = useCallback(() => {
        setRenewError('');
        clearStaleFee();
    }, [clearStaleFee]);

    // «ثبت‌نام با شهریه جدید»: archive the settled old enrollment, then hand the
    // secretary a prefilled registration form that freezes the current course fee.
    const handleRenewAtNewFee = useCallback(async () => {
        if (!selectedEnrollment) return;
        const remained = Number(selectedEnrollment.remained_sessions) || 0;
        if (remained !== 0) {
            setRenewError(
                `برای ثبت‌نام با شهریه جدید ابتدا باید این ثبت‌نام تسویه شود (جلسات باقی‌مانده: ${toPersianDigits(String(remained))}).`
            );
            return;
        }
        setRenewSubmitting(true);
        setRenewError('');
        try {
            await api.patch(`/operations/api/v1/student-registrations/${selectedEnrollment.id}/archive/`);
            navigate('/dashboard/student-schedule', { state: { renewEnrollment: selectedEnrollment } });
        } catch (err) {
            const msg = err?.response?.data?.detail || 'خطا در بایگانی ثبت‌نام قبلی';
            setRenewError(typeof msg === 'string' ? msg : 'خطا در بایگانی ثبت‌نام قبلی');
        } finally {
            setRenewSubmitting(false);
        }
    }, [selectedEnrollment, navigate]);

    const openDeleteDialog = useCallback((operationId) => {
        setOperationToDelete(operationId);
        setDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setOperationToDelete(null);
    }, []);

    const confirmDeleteOperation = useCallback(async () => {
        if (!operationToDelete) return;
        setOpDeleting(true);
        try {
            const success = await deleteOperation(operationToDelete);
            if (success) {
                // Refetch operations to get recalculated remained_sessions from backend
                if (selectedEnrollment?.id) {
                    await fetchOperations(selectedEnrollment.id);
                }
                // Refresh student card list and reselect the current enrollment with updated remained_sessions
                const updatedData = await fetchData();
                if (selectedEnrollment?.id && updatedData?.results) {
                    const updatedEnrollment = updatedData.results.find(e => e.id === selectedEnrollment.id);
                    if (updatedEnrollment) {
                        setSelectedEnrollment(updatedEnrollment);
                    }
                }
            }
            setDeleteMessage({ type: 'success', text: 'ردیف با موفقیت حذف شد' });
        } catch (err) {
            setDeleteMessage({ type: 'error', text: 'خطا در حذف ردیف' });
        } finally {
            setOpDeleting(false);
            closeDeleteDialog();
        }
    }, [operationToDelete, deleteOperation, selectedEnrollment, fetchOperations, fetchData, closeDeleteDialog]);

    if (error) {
        return (
            <RTL>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="error">خطا در دریافت اطلاعات: {error}</Typography>
                </Box>
            </RTL>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {selectedEnrollment && (
                        <Button startIcon={<BackIcon />} variant="outlined" onClick={() => setSelectedEnrollment(null)}>
                            بازگشت به فهرست
                        </Button>
                    )}
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        حضور و غیاب
                    </Typography>
                </Box>

                {!selectedEnrollment ? (
                    <>
                        <FilterPanel
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onDayChange={handleDayChange}
                            onCourseChange={handleCourseChange}
                            onClearFilters={handleClearFilters}
                            hasActiveFilters={hasActiveFilters()}
                        />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                تعداد نتایج: {toPersianDigits(count.toString())}
                            </Typography>
                        </Box>

                        {loading ? (
                            <CardGrid container spacing={2}>
                                {[...Array(6)].map((_, i) => (
                                    <Grid item xs={12} sm={6} md={4} key={`student-skeleton-${i}`}>
                                        <StudentCardBox>
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <Skeleton variant="text" width={120} />
                                                    </Box>
                                                </Box>
                                                <Skeleton variant="text" width={100} />
                                                <Divider sx={{ my: 1.5, opacity: 0.12 }} />
                                                <Skeleton variant="text" width={160} />
                                                <Skeleton variant="text" width={140} />
                                                <Skeleton variant="rectangular" height={28} sx={{ borderRadius: 1, mt: 1 }} />
                                                <Skeleton variant="text" width={180} sx={{ mt: 1 }} />
                                            </CardContent>
                                        </StudentCardBox>
                                    </Grid>
                                ))}
                            </CardGrid>
                        ) : enrollments.length === 0 ? (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    هیچ ثبت‌نامی یافت نشد.
                                </Typography>
                            </Box>
                        ) : (
                            <CardGrid container spacing={2}>
                                {enrollments.map((enrollment) => (
                                    <Grid item xs={12} sm={6} md={4} key={enrollment.id}>
                                        <EnrollmentCard
                                            enrollment={enrollment}
                                            onClick={() => handleOpenOperation(enrollment)}
                                        />
                                    </Grid>
                                ))}
                            </CardGrid>
                        )}
                    </>
                ) : (
                    <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'border.main', bgcolor: 'background.default' }}>
                        <OperationForm
                            selectedEnrollment={selectedEnrollment}
                            opForm={opForm}
                            opError={opError}
                            opSubmitting={opSubmitting}
                            isManager={isManager}
                            onFormChange={handleOpChange}
                            onSubmit={submitOperation}
                        />

                        <OperationsTable
                            operations={operations}
                            loading={operationsLoading}
                            error={operationsError}
                            onDelete={openDeleteDialog}
                            showSessionAmount={!isManager}
                            totalCount={operationsCount}
                            page={operationsPage}
                            rowsPerPage={operationsPageSize}
                            onPageChange={setOperationsPage}
                            onRowsPerPageChange={setOperationsPageSize}
                            statusFilter={operationsStatusFilter}
                            onStatusFilterChange={setOperationsStatusFilter}
                        />
                    </Paper>
                )}
            </Box>
            <FloatingSnackbar 
                open={Boolean(opMessage.text)} 
                message={opMessage.text} 
                severity={opMessage.type || 'info'} 
                onClose={clearOpMessage}
                anchor={{ vertical: 'bottom', horizontal: 'left' }}
                autoHideDuration={5000}
            />
            <FloatingSnackbar 
                open={Boolean(deleteMessage.text)} 
                message={deleteMessage.text} 
                severity={deleteMessage.type || 'info'} 
                onClose={() => setDeleteMessage({ type: '', text: '' })}
                anchor={{ vertical: 'bottom', horizontal: 'left' }}
                autoHideDuration={5000}
            />
            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                onConfirm={confirmDeleteOperation}
                title="تأیید حذف"
                message="آیا مطمئن هستید که می‌خواهید این ردیف را حذف کنید؟"
                confirmColor="error"
                isLoading={opDeleting}
            />
            <Dialog open={Boolean(staleFee)} onClose={closeStaleFeeDialog} dir="rtl" maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>تغییر شهریه دوره</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {`شهریه این دوره از ${toPersianDigits(new Intl.NumberFormat('fa-IR').format(staleFee?.enrollmentFee || 0))} تومان به ${toPersianDigits(new Intl.NumberFormat('fa-IR').format(staleFee?.currentFee || 0))} تومان تغییر کرده است. ثبت‌نام جدید این هنرجو با کدام شهریه انجام شود؟`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        با انتخاب «شهریه جدید»، ثبت‌نام فعلی بایگانی می‌شود و ثبت‌نام جدیدی با شهریه روز انجام می‌گیرد.
                    </Typography>
                    {renewError && <Alert severity="warning" sx={{ mt: 2 }}>{renewError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={closeStaleFeeDialog} disabled={renewSubmitting}>انصراف</Button>
                    <Button
                        variant="outlined"
                        onClick={() => { setRenewError(''); submitWithOldFee(); }}
                        disabled={renewSubmitting || opSubmitting}
                    >
                        ثبت‌نام با شهریه قدیم
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRenewAtNewFee}
                        disabled={renewSubmitting || opSubmitting}
                    >
                        ثبت‌نام با شهریه جدید
                    </Button>
                </DialogActions>
            </Dialog>
        </RTL>
    );
}

